import React from 'react';
import { XIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon } from 'lucide-react';
interface StatusFlowModalProps {
  settlement: {
    id: string;
    status: string;
    statusHistory?: any[];
  };
  onClose: () => void;
}
export const StatusFlowModal: React.FC<StatusFlowModalProps> = ({
  settlement,
  onClose
}) => {
  // Status flow steps
  const statusFlow = [{
    id: 'initiated',
    label: 'Initiated',
    description: 'Settlement created',
    icon: <ClockIcon className="h-5 w-5" data-id="element-787" />
  }, {
    id: 'under_review',
    label: 'Under Review',
    description: 'Settlement being verified',
    icon: <AlertCircleIcon className="h-5 w-5" data-id="element-788" />
  }, {
    id: 'approved',
    label: 'Approved',
    description: 'Settlement approved for payment',
    icon: <CheckCircleIcon className="h-5 w-5" data-id="element-789" />
  }, {
    id: 'processing',
    label: 'Processing',
    description: 'Payment being processed',
    icon: <ClockIcon className="h-5 w-5" data-id="element-790" />
  }, {
    id: 'completed',
    label: 'Completed',
    description: 'Payment completed',
    icon: <CheckCircleIcon className="h-5 w-5" data-id="element-791" />
  }];
  // Get status info from history
  const getStatusInfo = statusId => {
    const historyItem = settlement.statusHistory?.find(item => item.status === statusId);
    return historyItem || null;
  };
  // Get current status index
  const getCurrentStatusIndex = () => {
    return statusFlow.findIndex(step => step.id === settlement.status);
  };
  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleBackdropClick} data-id="element-792">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-793">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-794">
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-795">
            Settlement Status Flow
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-796">
            <XIcon className="h-5 w-5" data-id="element-797" />
          </button>
        </div>
        {/* Content */}
        <div className="px-6 py-8 overflow-y-auto max-h-[calc(90vh-8rem)]" data-id="element-798">
          <div className="mb-6" data-id="element-799">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-800">
              Settlement: {settlement.id}
            </h3>
            <p className="text-gray-500" data-id="element-801">
              Current Status:{' '}
              <span className="font-medium" data-id="element-802">
                {statusFlow.find(step => step.id === settlement.status)?.label || settlement.status}
              </span>
            </p>
          </div>
          <div className="relative" data-id="element-803">
            {/* Connecting Line */}
            <div className="absolute left-8 top-8 h-[calc(100%-32px)] w-0.5 bg-gray-200" data-id="element-804"></div>
            {/* Status Steps */}
            <div className="space-y-8" data-id="element-805">
              {statusFlow.map((step, index) => {
              const statusInfo = getStatusInfo(step.id);
              const isCompleted = index <= getCurrentStatusIndex();
              const isActive = step.id === settlement.status;
              return <div key={step.id} className="relative flex items-start" data-id="element-806">
                    <div className={`absolute flex items-center justify-center w-16 h-16 rounded-full ${isActive ? 'bg-blue-600 text-white ring-4 ring-blue-100' : isCompleted ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-807">
                      {step.icon}
                    </div>
                    <div className="ml-24" data-id="element-808">
                      <h4 className={`text-lg font-medium ${isActive ? 'text-blue-600' : isCompleted ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-809">
                        {step.label}
                      </h4>
                      <p className="text-gray-500 mt-1" data-id="element-810">{step.description}</p>
                      {statusInfo && <div className="mt-3 text-sm" data-id="element-811">
                          <div className="flex items-center text-gray-600" data-id="element-812">
                            <span className="font-medium" data-id="element-813">Date:</span>
                            <span className="ml-2" data-id="element-814">
                              {statusInfo.date} {statusInfo.time}
                            </span>
                          </div>
                          <div className="flex items-center text-gray-600 mt-1" data-id="element-815">
                            <span className="font-medium" data-id="element-816">By:</span>
                            <span className="ml-2" data-id="element-817">{statusInfo.user}</span>
                          </div>
                          {statusInfo.description && <div className="mt-2 text-gray-600" data-id="element-818">
                              {statusInfo.description}
                            </div>}
                        </div>}
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-819">
          <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-820">
            Close
          </button>
        </div>
      </div>
    </div>;
};